import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { apiPost } from "../../../services/api/generic.api";
import Button from "../../atoms/Button/Button";
import TextField from "../../atoms/TextField/TextField";
import classNames from "classnames";
import SendIcon from "@mui/icons-material/Send";
import RichTextEditor from "../RichTextEditor/RichTextEditor";
import { supabase } from "../../../lib/supabase/supabaseClient";
import { useSelector } from "react-redux";

export type Message = {
  role: "user" | "assistant";
  content: string;
  partial?: boolean;
  citations?: Citation[];
};

export type Citation = {
  date: string;
  title: string;
  url: string;
};

interface AiChatbotProps {
  sidebarItemName?: string;
  formattedReport?: string;
}

export default function Chat(props: AiChatbotProps) {
  const { formattedReport, sidebarItemName } = props;

  const userId = useSelector((state: any) => state.settings.user.userID);

  const defaultMessages: Message[] = [
    {
      role: "assistant",
      content: sidebarItemName
        ? `Ahoj, mám všetky informácie z lekárkej správy. Môžeš sa ma spýtať doplňujúce otázky k vyšetreniu: ${sidebarItemName}.`
        : "Ahoj, ako ti môžem pomôcť?",
    },
  ];

  const dispatch = useDispatch();

  const [messages, setMessages] = useState<Message[]>(defaultMessages);
  const [input, setInput] = useState("");
  const [isAnswering, setIsAnswering] = useState(false);
  const channelId = `chat-room-${userId}`;

  const scrollToBottom = () => {
    const messagesElement = document.getElementById("dui-chat-messages");
    if (messagesElement) {
      messagesElement.scrollTo(0, messagesElement.scrollHeight);
    }
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const channel = supabase.channel(channelId, {
      config: { broadcast: { self: true } },
    });

    channel.on("broadcast", { event: "message" }, (payload) => {
      const data = payload.payload;
      if (!data) return;

      setMessages((prev) => {
        if (data.role === "assistant" && data.partial) {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant") {
            if (last.content.startsWith("Premýšľam...")) {
              return [...prev.slice(0, -1), { ...last, content: data.content }];
            }
            const newMessages = [
              ...prev.slice(0, -1),
              { ...last, content: last.content + data.content },
            ];
            return newMessages;
          } else {
            return [...prev, data];
          }
        }
        if (data.role === "user") {
          return [
            ...prev,
            data,
            {
              role: "assistant",
              content: "Premýšľam...",
            },
          ];
        }
        if (data.role === "assistant" && !data.partial) {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant") {
            setIsAnswering(false);
            return [
              ...prev.slice(0, -1),
              {
                ...data,
                content: last.content + data.content,
                citations: data.citations,
              },
            ];
          }
        }
        return [...prev, data];
      });
    });

    channel.subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [channelId]);

  const sendMessage = async () => {
    if (isAnswering) return;

    setInput("");
    setIsAnswering(true);
    if (!input && !input.trim()) return;

    await supabase.channel(channelId).httpSend("message", {
      role: "user",
      content: input,
    });

    const messagesWithoutFirstMessage = messages.filter(
      (msg, index) => index !== 0,
    );

    const newMessages = [
      ...messagesWithoutFirstMessage,
      { role: "user", content: input },
    ];

    // Trigger backend stream
    await apiPost(dispatch, "api/chat", {
      messages: newMessages,
      formattedReport,
      channelId,
    });
  };

  const handleResetConversation = () => {
    setInput("");
    setMessages(defaultMessages);
  };

  return (
    <div className="dui-chat">
      <div className="dui-chat-messages" id="dui-chat-messages">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={classNames("dui-chat-message", {
              "dui-chat-message-user": msg.role === "user",
              "dui-chat-message-bot": msg.role === "assistant",
            })}
          >
            <RichTextEditor markdown={msg.content} readonly />
            {/* {msg.citations && <ChatCitations citations={msg.citations} />} */}
          </div>
        ))}
      </div>
      {messages.length <= 10 ? (
        <div className="dui-chat-input">
          <TextField
            value={input}
            onChange={setInput}
            placeholder="Sem napíšte svoju správu..."
            onEnter={sendMessage}
          />
          <Button
            disabled={isAnswering}
            onClick={sendMessage}
            className="dui-chat-input-send"
            icon={<SendIcon />}
          ></Button>
        </div>
      ) : (
        <div className="dui-chat-limit-reached">
          <div>Presiahli ste maximálny počet správ na konverzáciu</div>
          <Button type="secondary" onClick={handleResetConversation}>
            Začať novú konverzáciu
          </Button>
        </div>
      )}
      <div className="dui-chat-disclaimer">
        Upozornenie: Odpovede nemusia byť vždy presné – overte si ich z
        dôveryhodných zdrojov.
      </div>
    </div>
  );
}
